'use client';

import { useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { SectionHeader, Grad } from '@/components/ui/Section';
import LeadCaptureModal from '@/components/common/LeadCaptureModal';
import { Button, ButtonLink } from '@/components/ui/Button';

type Result = {
  metric: string;
  before: string;
  after: string;
  improvement: string;
  icon: string;
};

type CaseStudy = {
  id: string;
  company: string;
  industry: string;
  tabIcon: string;
  challenge: string;
  solution: string;
  resultsLabel?: string;
  results: Result[];
  image: string;
  imageAlt: string;
  actionHref: string;
  actionExternal?: boolean;
  actionLabel: string;
};

const caseStudies: CaseStudy[] = [
  {
    id: 'study_automotive',
    company: 'Global Automotive Manufacturer',
    industry: 'Automotive Lubricants',
    tabIcon: 'TruckIcon',
    challenge: 'High wear rates in hydraulic systems causing frequent maintenance and downtime.',
    solution: 'Implemented Micratto graphene additives at 0.5% concentration in hydraulic fluids.',
    results: [
      {
        metric: 'Friction Coefficient',
        before: '0.1113',
        after: '0.0692',
        improvement: '-46.65%',
        icon: 'BeakerIcon',
      },
      {
        metric: 'Wear Scar Diameter',
        before: '525 μm',
        after: '371 μm',
        improvement: '-34.38%',
        icon: 'AdjustmentsHorizontalIcon',
      },
      {
        metric: 'Anti-Oxidation (OIT)',
        before: '3.04 min',
        after: '40.56 min',
        improvement: '+23.47%',
        icon: 'ShieldCheckIcon',
      },
    ],
    image: '/assets/images/home-lubricants.jpg',
    imageAlt: 'Automotive lubricant performance testing and mechanical components',
    actionHref: 'https://infinoil.com/',
    actionExternal: true,
    actionLabel: 'Find out more',
  },
  {
    id: 'study_construction',
    company: 'Major Infrastructure Developer',
    industry: 'High-Performance Concrete',
    tabIcon: 'BuildingOffice2Icon',
    challenge: 'Need for ultra-durable concrete for coastal bridge construction.',
    solution: 'Added 0.05% graphene nanoplatelets to concrete mix design.',
    resultsLabel: 'Quantitative results (M45 grade)',
    results: [
      {
        metric: 'Compressive Strength',
        before: '45 MPa',
        after: '61 MPa',
        improvement: '+35.6%',
        icon: 'CubeIcon',
      },
      {
        metric: 'Flexural Strength',
        before: '4.5 MPa',
        after: '6.8 MPa',
        improvement: '+27%',
        icon: 'ArrowsPointingOutIcon',
      },
      {
        metric: 'Water Permeability',
        before: '2.1×10⁻¹² m/s',
        after: '0.8×10⁻¹² m/s',
        improvement: '-61.9%',
        icon: 'CloudIcon',
      },
    ],
    image: '/assets/images/home-concrete.jpg',
    imageAlt: 'Coastal bridge construction showing reinforced high-performance concrete structures',
    actionHref: '/services',
    actionLabel: 'Find out more',
  },
  {
    id: 'study_aerospace',
    company: 'Aerospace Composites Manufacturer',
    industry: 'Advanced Composites',
    tabIcon: 'Square3Stack3DIcon',
    challenge: 'Thermal management issues in carbon fiber composites for aircraft components.',
    solution: 'Integrated graphene additives into epoxy resin matrix at 2% loading.',
    results: [
      {
        metric: 'Thermal Conductivity',
        before: '0.8 W/mK',
        after: '3.2 W/mK',
        improvement: '+300%',
        icon: 'BoltIcon',
      },
      {
        metric: 'Tensile Strength',
        before: '580 MPa',
        after: '720 MPa',
        improvement: '+24.1%',
        icon: 'ArrowsPointingOutIcon',
      },
      {
        metric: 'Weight',
        before: '2.4 kg/m²',
        after: '2.0 kg/m²',
        improvement: '-16.7%',
        icon: 'ScaleIcon',
      },
    ],
    image: '/assets/images/home-composites.png',
    imageAlt: 'Layered carbon-fiber composite material with graphene reinforcement',
    actionHref: '/services',
    actionLabel: 'Find out more',
  },
];

const CaseStudies = () => {
  const [selectedStudy, setSelectedStudy] = useState(caseStudies[0].id);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const activeStudy = caseStudies.find((study) => study.id === selectedStudy) ?? caseStudies[0];

  const selectTab = (index: number) => {
    setSelectedStudy(caseStudies[index].id);
    tabRefs.current[index]?.focus();
  };

  const onTabKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = caseStudies.findIndex((study) => study.id === selectedStudy);
    let nextIndex: number | null = null;

    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % caseStudies.length;
    if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + caseStudies.length) % caseStudies.length;
    }
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = caseStudies.length - 1;

    if (nextIndex !== null) {
      event.preventDefault();
      selectTab(nextIndex);
    }
  };

  return (
    <>
      <section id="case-studies" className="section relative overflow-hidden bg-ink text-white">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(60rem_42rem_at_26%_34%,rgba(14,165,233,0.14),transparent_62%),radial-gradient(52rem_34rem_at_82%_82%,rgba(14,165,233,0.08),transparent_68%),linear-gradient(155deg,#0c1830_0%,#060c18_56%,#0b1a32_100%)]"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.035] [background-image:linear-gradient(rgba(14,165,233,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(14,165,233,0.5)_1px,transparent_1px)] [background-size:48px_48px]"
        />

        <div className="container-page relative">
          <SectionHeader
            title={
              <>
                Proven <Grad>Performance</Grad>
              </>
            }
            eyebrow="Real-world results"
            lead="Quantitative results from leading manufacturers across multiple industries"
          />

          <div
            role="tablist"
            aria-label="Case study industries"
            onKeyDown={onTabKeyDown}
            className="scroll-x mx-auto mt-9 flex max-w-5xl gap-2 pb-2 sm:mt-10 sm:grid sm:grid-cols-3 sm:overflow-visible sm:pb-0"
          >
            {caseStudies.map((study, index) => {
              const isActive = study.id === selectedStudy;
              return (
                <button
                  key={study.id}
                  ref={(element) => {
                    tabRefs.current[index] = element;
                  }}
                  type="button"
                  role="tab"
                  id={`case-tab-${study.id}`}
                  aria-selected={isActive}
                  aria-controls={`case-panel-${study.id}`}
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => setSelectedStudy(study.id)}
                  className={`flex min-h-[58px] min-w-[250px] items-center justify-center gap-3 rounded-[10px] border px-5 text-left text-[0.75rem] font-semibold uppercase tracking-[0.025em] transition-[background-color,border-color,box-shadow,color] duration-base sm:min-w-0 lg:text-[0.8125rem] ${
                    isActive
                      ? 'bg-accent-rich border-transparent text-white shadow-[0_10px_30px_rgba(14,165,233,0.3),inset_0_1px_0_rgba(255,255,255,0.18)]'
                      : 'border-accent/30 bg-[#0a1322]/80 text-slate-300 hover:border-accent/60 hover:bg-accent/10 hover:text-white'
                  }`}
                >
                  <Icon
                    name={study.tabIcon}
                    size={23}
                    className={isActive ? 'text-white' : 'text-slate-400'}
                  />
                  <span>{study.industry}</span>
                </button>
              );
            })}
          </div>

          <div
            key={activeStudy.id}
            role="tabpanel"
            id={`case-panel-${activeStudy.id}`}
            aria-labelledby={`case-tab-${activeStudy.id}`}
            className="case-study-swap mt-8 grid min-w-0 gap-8 lg:grid-cols-[minmax(0,1.08fr)_minmax(0,1fr)] lg:items-stretch lg:gap-8"
          >
            <div className="relative min-h-[510px] overflow-hidden rounded-[18px] border border-accent/35 bg-[#0a1322] shadow-[0_26px_70px_rgba(0,5,18,0.45),inset_0_0_60px_rgba(14,165,233,0.06)] sm:min-h-[620px] lg:min-h-0">
              <div
                aria-hidden="true"
                className="absolute inset-0 bg-[radial-gradient(circle_at_50%_42%,rgba(14,165,233,0.26),transparent_40%),linear-gradient(180deg,rgba(2,19,49,0.25),rgba(1,9,25,0.9))]"
              />
              <div className="absolute inset-0">
                <AppImage
                  src={activeStudy.image}
                  alt={activeStudy.imageAlt}
                  fill
                  sizes="(max-width: 1024px) 100vw, 52vw"
                  className="case-image-swap h-full w-full object-cover opacity-75"
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-0 bg-[linear-gradient(180deg,rgba(1,13,34,0.05),rgba(2,14,34,0.72))]"
                />
              </div>

              <div className="absolute bottom-5 left-5 right-5 rounded-xl border border-accent/40 bg-[#0a1322]/95 px-5 py-4 shadow-[0_12px_35px_rgba(0,5,20,0.35)] sm:bottom-8 sm:left-auto sm:right-8 sm:w-[48%] sm:px-6 sm:py-5">
                <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.12em] text-accent">
                  Industry
                </p>
                <div className="mt-2 flex items-center justify-between gap-4">
                  <p className="text-base font-semibold text-white sm:text-lg">
                    {activeStudy.industry}
                  </p>
                  <Icon name={activeStudy.tabIcon} size={30} className="shrink-0 text-accent" />
                </div>
              </div>
            </div>

            <div className="flex min-w-0 flex-col lg:py-4">
              <h3 className="text-[1.65rem] font-semibold leading-tight tracking-[-0.02em] text-white sm:text-[2rem]">
                {activeStudy.company}
              </h3>

              <dl className="mt-6 grid gap-5">
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-[0.07em] text-accent">
                    Challenge
                  </dt>
                  <dd className="mt-2 text-sm leading-7 text-slate-300">{activeStudy.challenge}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-[0.07em] text-accent">
                    Solution
                  </dt>
                  <dd className="mt-2 text-sm leading-7 text-slate-300">{activeStudy.solution}</dd>
                </div>
              </dl>

              <div className="mt-6 overflow-hidden rounded-[16px] border border-accent/35 bg-[#0a1322]/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.025)]">
                <h4 className="px-5 pb-2 pt-5 text-[0.75rem] font-semibold uppercase tracking-[0.05em] text-accent sm:px-6">
                  {activeStudy.resultsLabel ?? 'Quantitative results'}
                </h4>
                <div className="px-4 pb-2 sm:px-5">
                  {activeStudy.results.map((result) => (
                    <div
                      key={result.metric}
                      className="group/result grid min-w-0 grid-cols-[44px_minmax(0,1fr)] items-center gap-3 border-b border-white/10 px-1 py-4 transition-colors duration-base last:border-0 hover:bg-accent/[0.07] sm:grid-cols-[46px_minmax(0,1fr)_auto] sm:gap-4"
                    >
                      <span className="flex h-11 w-11 items-center justify-center rounded-full border border-accent/50 bg-accent/10 text-sky-400">
                        <Icon name={result.icon} size={21} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white">{result.metric}</p>
                        <p className="t-value mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[0.75rem] text-slate-400 sm:text-[0.8125rem]">
                          <span>
                            Before: <span className="text-slate-300">{result.before}</span>
                          </span>
                          <Icon name="ArrowRightIcon" size={14} className="text-slate-500" />
                          <span>
                            After: <span className="text-sky-400">{result.after}</span>
                          </span>
                        </p>
                      </div>
                      <div className="col-start-2 flex items-center gap-2 sm:col-start-auto sm:justify-self-end">
                        <span className="t-value text-lg font-semibold text-[#5eea78]">
                          {result.improvement}
                        </span>
                        <Icon
                          name={
                            result.improvement.startsWith('+') ? 'ArrowUpIcon' : 'ArrowDownIcon'
                          }
                          size={18}
                          className="text-[#5eea78]"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <ButtonLink
                  href={activeStudy.actionHref}
                  external={activeStudy.actionExternal}
                  variant="primary"
                  size="lg"
                  className="group h-14 w-full rounded-[10px] bg-accent uppercase tracking-[0.06em] shadow-[0_2px_14px_rgba(14,165,233,0.35),inset_0_1px_0_rgba(255,255,255,0.2)] hover:bg-sky-400"
                >
                  {activeStudy.actionLabel}
                  <Icon
                    name="ArrowRightIcon"
                    size={19}
                    className="transition-transform duration-base group-hover:translate-x-[3px] motion-reduce:transform-none"
                  />
                </ButtonLink>
                <Button
                  variant="light"
                  size="lg"
                  onClick={() => setIsModalOpen(true)}
                  className="group h-14 w-full rounded-[10px] border-accent/35 bg-white/[0.03] uppercase tracking-[0.06em] hover:border-accent/60 hover:bg-accent/10"
                >
                  Read full case study
                  <Icon name="DocumentTextIcon" size={19} className="text-slate-300" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LeadCaptureModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        resourceId="graphene-case-study"
        buttonText="Download Case Study"
        modalTitle="Download Case Study"
        modalDescription="Enter your details to download our case study document"
        successMessage="Thank You! Your case study is downloading..."
      />
    </>
  );
};

export default CaseStudies;
